import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, ChevronRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import PartnerForm from "../components/PartnerForm";
import { Button, Spinner } from "../components/ui/kit";
import { useAuth } from "../context/AuthContext";
import { canSee } from "../lib/nav";
import { fmtKc } from "../lib/format";
import api from "../lib/api";
import { useUrlStav } from "../lib/navigace";

/** Iniciály do dlaždice — firmy bez loga. */
export function inicialy(nazev) {
  return (nazev || "?").replace(/[,.]|s\.r\.o|a\.s/gi, " ").split(/\s+/).filter(Boolean)
    .slice(0, 2).map((s) => s[0].toUpperCase()).join("");
}

export default function Partneri() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canWrite = canSee(user, ["OWNER", "OBCHODNIK"]);
  const [data, setData] = useState(null);
  const [q, setQ] = useUrlStav("q", "");
  const [form, setForm] = useState(false);

  const load = useCallback(async () => {
    const { data: d } = await api.get("/api/partneri", { params: { q } });
    setData(d);
  }, [q]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const items = data?.items;
  const money = data?.muze_ceny;
  const sloupce = money
    ? "md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_90px_90px_130px_24px]"
    : "md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_90px_90px_24px]";

  return (
    <div data-testid="partneri-page">
      <PageHeader eyebrow="Obchod / Obchodní partneři" title="Obchodní partneři"
        subtitle="Firmy, které ti dohazují klienty — a kolik zakázek od nich máš."
        actions={canWrite && (
          <Button onClick={() => setForm(true)} data-testid="new-partner">
            <Plus size={16} strokeWidth={2} /> Nový partner
          </Button>
        )} />

      <div className="mb-4 md:max-w-sm">
        <div className="flex items-center gap-2 field">
          <Search size={15} className="text-muted shrink-0" />
          <input className="bg-transparent outline-none w-full" placeholder="Hledat firmu, IČO, kontakt…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="partneri-search" />
        </div>
      </div>

      <div className="card overflow-hidden">
        {items === undefined ? (
          <div className="p-10 flex justify-center text-muted"><Spinner /></div>
        ) : items.length === 0 ? (
          <EmptyState icon="Handshake" title={q ? "Nic nenalezeno" : "Zatím žádní partneři"}
            description={q ? "Zkus jiné hledání." : "Založ firmu, která ti posílá klienty — architekta, stavební firmu, developera."}
            action={canWrite && !q && (
              <Button onClick={() => setForm(true)} data-testid="new-partner-empty"><Plus size={16} /> Nový partner</Button>
            )} />
        ) : (
          <>
            <div className={`hidden md:grid ${sloupce} gap-3 px-4 py-2.5 label-up text-muted border-b border-line`}>
              <span>Partner</span><span>Kontaktní osoba</span>
              <span className="text-right">Klientů</span><span className="text-right">Případů</span>
              {money && <span className="text-right">Obrat</span>}<span />
            </div>
            {items.map((p) => {
              const o = p.hlavni_osoba;
              const kontakt = o ? [o.jmeno, o.prijmeni].filter(Boolean).join(" ") : "—";
              return (
                <button key={p.id} onClick={() => nav(`/partneri/${p.id}`)} data-testid={`partner-row-${p.id}`}
                  className={`w-full text-left grid grid-cols-[1fr_24px] ${sloupce} gap-3 items-center px-4 py-3.5 border-b border-line-softer last:border-0 hover:bg-inputbg transition-colors`}>
                  <span className="flex items-center gap-3 min-w-0">
                    {p.logo_url ? (
                      <span className="h-10 w-10 rounded-[11px] bg-white shadow-[inset_0_0_0_1px_#eceae5] flex items-center justify-center overflow-hidden shrink-0">
                        <img src={p.logo_url} alt="" className="h-full w-full object-contain" />
                      </span>
                    ) : (
                      <span className="h-10 w-10 rounded-[11px] bg-ink text-white t-meta font-700 flex items-center justify-center shrink-0">
                        {inicialy(p.nazev)}
                      </span>
                    )}
                    <span className="min-w-0">
                      <span className="block t-telo font-700 text-ink truncate">{p.nazev}</span>
                      <span className="block t-meta text-muted truncate">
                        {[p.adresa?.ulice, p.adresa?.mesto].filter(Boolean).join(", ") || "sídlo neuvedeno"}
                      </span>
                      {/* mobil: čísla pod názvem místo sloupců */}
                      <span className="md:hidden block t-meta text-sec-3 mt-0.5">
                        <span className="amount">{p.klientu}</span> klientů · <span className="amount">{p.pripadu}</span> případů
                        {money && <> · <span className="amount font-600 text-ink">{fmtKc(p.obrat_kc)} Kč</span></>}
                      </span>
                    </span>
                  </span>
                  <span className="hidden md:block min-w-0">
                    <span className="block t-telo text-sec truncate">{kontakt}</span>
                    {o?.telefon && <span className="block t-meta text-muted amount truncate">{o.telefon}</span>}
                  </span>
                  <span className="hidden md:block text-right amount t-telo font-600 text-ink">{p.klientu}</span>
                  <span className="hidden md:block text-right amount t-telo text-sec">{p.pripadu}</span>
                  {money && <span className="hidden md:block text-right amount t-telo font-600 text-ink">{fmtKc(p.obrat_kc)} Kč</span>}
                  <ChevronRight size={16} className="text-muted justify-self-end" />
                </button>
              );
            })}
          </>
        )}
      </div>

      {form && <PartnerForm onClose={() => setForm(false)} onSaved={(d) => nav(`/partneri/${d.id}`)} />}
    </div>
  );
}
